import { InfoMenu } from "@src/components/elements/InfoMenu";
import { SelectOption } from "@src/components/elements/Select/SelectOption";
import { SelectProps } from "@src/components/elements/Select/SelectProps";
import { SelectStyledRoot } from "@src/components/elements/Select/SelectStyledRoot";
import React, { ReactElement, Ref } from "react";
import ReactSelect, {
  ActionMeta,
  MultiValue,
  StylesConfig,
} from "react-select";
import CreatableSelect from "react-select/creatable";
import Select from "react-select/dist/declarations/src/Select";

export const multiStyles = (
  error?: string,
  size?: "large" | "small",
  labelType?: "normal" | "floating",
): StylesConfig<SelectOption, true> => {
  return {
    menuPortal: (provided) => {
      return {
        ...provided,
        zIndex: 100000,
      };
    },
    option: (provided, state) => ({
      ...provided,
      cursor: "pointer",
      border: "none",
      "&:hover": {
        color: "var(--dark-green)",
        background: "var(--background-green)",
      },
      "&:active": { background: "white" },
      background: "white",
      color: state.isSelected
        ? "var(--green) !important"
        : "var(--primary-black)",
      fontFamily: "var(--inter-font)",
      fontSize: "var(--font-size-small)",
      padding: "0.625rem 0.75rem",
      wordWrap: "break-word",
    }),
    control: (provided, state) => {
      return {
        ...provided,
        color: "white",
        border: state.isFocused
          ? `1px solid ${error ? "red" : "transparent"}`
          : `1px solid ${error ? "red" : "var(--border-gray)"}`,
        outline: "none",
        boxShadow: state.isFocused ? "0 0 0 2px var(--green)" : "none",
        "&:hover": {
          border: "1px solid var(--dark-gray)",
        },
        padding: "0.2em 0.4em",
      };
    },
    menuList: (provided) => {
      return {
        ...provided,
        background: "white",
        color: "var(--primary-black)",
        border: "1px solid transparent",
        padding: "0.5em 0em",
        borderRadius: "var(--rounded-corner)",
      };
    },
    input: (provided) => {
      return {
        ...provided,
        color: "var(--primary-black)",
        fontFamily: "var(--inter-font)",
      };
    },
    placeholder: (provided) => {
      return {
        ...provided,
        color:
          labelType === "normal"
            ? "var(--placeholder-color)"
            : "var(--border-gray)",
        fontFamily: "var(--inter-font)",
        fontSize: "1em",
      };
    },
    singleValue: (provided) => {
      return {
        ...provided,
        fontFamily: "var(--inter-font)",
        fontSize: size === "small" ? "0.8em" : "1em",
      };
    },
    valueContainer: (provided) => {
      return {
        ...provided,
        height: "100%",
        padding: "0.125rem 0.5rem",
      };
    },
    indicatorsContainer: (provided) => {
      return {
        ...provided,
        height: "100%",
      };
    },
    multiValue: (provided) => {
      return {
        ...provided,
        transition: "var(--transition)",
        background: "var(--background-gray)",
        fontFamily: "var(--inter-font)",
        border: "1px solid var(--border-gray)",
        borderRadius: "1rem",
        lineHeight: "1.2em",
      };
    },
    multiValueLabel: (provided) => {
      return {
        ...provided,
        fontFamily: "var(--inter-font)",
        color: "var(--gray)",
        textWrap: "wrap",
        "&:hover": {},
      };
    },
    multiValueRemove: (provided) => {
      return {
        ...provided,
        fontFamily: "var(--inter-font)",
        color: "var(--gray)",
        "&:hover": {
          background: "transparent",
          color: "var(--secondary-black)",
        },
      };
    },
    indicatorSeparator: (provided) => {
      return {
        ...provided,
        backgroundColor: "var(--secondary-black)",
        opacity: 0.1,
      };
    },
  };
};

export type MultiSelectProps = Omit<
  SelectProps<true>,
  "onChange" | "inputRef" | "value" | "options"
> & {
  options: readonly SelectOption[];
  inputRef?: Ref<Select<SelectOption, true>>;
  onChange?:
    | ((
        newValue: MultiValue<SelectOption>,
        actionMeta: ActionMeta<SelectOption>,
      ) => void)
    | undefined;
  value: SelectOption[] | null;
};

export const MultiSelect: React.FC<MultiSelectProps> = React.forwardRef(
  (
    {
      inputRef,
      isCreatable,
      isClearable,
      isLoading,
      placeholder,
      error,
      size,
      options,
      isDisabled,
      onChange,
      value,
      menuPortalTarget,
      onCreateOption,
      horizontalLabel,
      className,
      label,
      required,
      tooltipText,
      dropdownClassName,
      labelType = "normal",
      disableError = false,
    },
    ref: Ref<Select<SelectOption, true>>,
  ): ReactElement => {
    const styles = multiStyles(error, size);
    const [isFocused, setIsFocused] = React.useState(false);
    const labelShouldFloat =
      labelType === "floating" && (isFocused || (!!value && value.length > 0));

    return (
      <SelectStyledRoot
        labelShouldFloat={labelShouldFloat}
        horizontalLabel={horizontalLabel}
        className={className}
      >
        {label && labelType === "normal" && (
          <div className="label-wrap">
            <label htmlFor={label}>
              {label} {required && <span className="error">*</span>}{" "}
            </label>
            {tooltipText && <InfoMenu title={<span>{tooltipText}</span>} />}
          </div>
        )}
        <div className="input-container">
          {!isCreatable ? (
            <ReactSelect<SelectOption, true>
              ref={inputRef || ref}
              isClearable={isClearable}
              isMulti={true}
              value={value}
              isLoading={isLoading}
              placeholder={placeholder}
              styles={styles}
              options={options}
              isDisabled={isDisabled}
              menuShouldBlockScroll={true}
              onChange={onChange}
              menuPortalTarget={menuPortalTarget}
              className={`select-input ${dropdownClassName}`}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              menuPlacement="auto"
              closeMenuOnSelect={false}
            />
          ) : (
            <CreatableSelect
              isMulti={true}
              ref={inputRef || ref}
              isClearable={isClearable}
              value={value}
              isDisabled={isDisabled}
              isLoading={isLoading}
              placeholder={placeholder}
              styles={styles}
              options={options}
              menuShouldBlockScroll={true}
              onChange={onChange}
              onCreateOption={onCreateOption}
              menuPortalTarget={menuPortalTarget}
              className={`select-input ${dropdownClassName}`}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              menuPlacement="auto"
              closeMenuOnSelect={false}
            />
          )}
          {labelType === "floating" ? (
            <div className="label-wrap">
              <label className="floating-label" htmlFor={label}>
                {label}
                {required && <span className="error">*</span>}
              </label>
            </div>
          ) : undefined}
        </div>
        {!disableError && <div className="error">{error}&nbsp;</div>}
      </SelectStyledRoot>
    );
  },
);

MultiSelect.displayName = "MultiSelect";
